import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { appConfig } from 'src/config';
import { CDR, CDRStatus } from './portal';
import { PaginatedResponse } from './types';
import { Campaign } from 'src/types/campaign';
import { Contact } from 'src/types/contact';
import countries from 'src/library/MobilePhone/countries.json';
import { withSessionExpiredHandler } from 'src/api/common/baseQueryWithAuth';

export interface ContactWithCampaign {
  agentId: number;
  campaignId: number | null;
  campaignName: null | string;
  countUnread: number;
  contactId?: number | null;
  foundInContacts: boolean;
  lastMsgTime: string;
  name: string;
  number: string;
  /** Present only in the bulk "all sender IDs" response — distinguishes which sender the row belongs to. */
  senderNumber?: string;
}

export interface ContactFilters {
  tags: string[];
  dialCodes: { code: string; dialCode: string; name: string }[];
}

export interface CampaignFilters {
  channels: string[];
  creationDate: {
    from: string;
    to: string;
  };
  runDate: {
    from: string;
    to: string;
  };
  status: string[];
}

export interface SMSCDRFilters {
  senderIds: string[];
  destinations: string[];
  services: string[];
  statuses: CDRStatus[];
}

type CountryDialCode = {
  code: string;
  dial_code: string;
  name: string;
};

const buildFallbackDialCodes = (): ContactFilters['dialCodes'] => {
  const dialCodeMap = new Map<string, ContactFilters['dialCodes'][number]>();

  (countries as CountryDialCode[]).forEach((country) => {
    const dialCode = country.dial_code.replace(/\D/g, '');
    if (!dialCode || dialCodeMap.has(dialCode)) {
      return;
    }

    dialCodeMap.set(dialCode, {
      code: country.code,
      dialCode,
      name: country.name,
    });
  });

  return [...dialCodeMap.values()].sort((left, right) => Number(left.dialCode) - Number(right.dialCode));
};

const normalizeLegacyTagNames = (response: unknown): string[] => {
  if (Array.isArray(response)) {
    return response
      .map((tag) => {
        if (typeof tag === 'string') {
          return tag;
        }

        if (typeof tag === 'object' && tag !== null && 'name' in tag && typeof tag.name === 'string') {
          return tag.name;
        }

        return null;
      })
      .filter((tag): tag is string => Boolean(tag));
  }

  if (typeof response === 'object' && response !== null) {
    const embedded = (response as { _embedded?: { contact_tag?: unknown[] } })._embedded;
    const contactTags = embedded?.contact_tag;
    if (Array.isArray(contactTags)) {
      return contactTags
        .map((tag) =>
          typeof tag === 'object' && tag !== null && 'name' in tag && typeof tag.name === 'string'
            ? tag.name
            : null
        )
        .filter((tag): tag is string => Boolean(tag));
    }
  }

  return [];
};

const normalizeTagsFromContactsResponse = (response: unknown): string[] => {
  if (typeof response !== 'object' || response === null) {
    return [];
  }

  const contacts = (response as { _embedded?: { contacts?: unknown[] } })._embedded?.contacts;
  if (!Array.isArray(contacts)) {
    return [];
  }

  const tagNames = new Set<string>();

  contacts.forEach((contact) => {
    if (typeof contact !== 'object' || contact === null) {
      return;
    }

    const contactTags = (contact as { contactTags?: unknown[] }).contactTags;
    if (!Array.isArray(contactTags)) {
      return;
    }

    contactTags.forEach((tag) => {
      if (typeof tag === 'object' && tag !== null && 'name' in tag && typeof tag.name === 'string') {
        tagNames.add(tag.name);
      }
    });
  });

  return [...tagNames].sort();
};

const sanitizeContactFilters = (filters: ContactFilters | null | undefined): ContactFilters => ({
  tags: Array.isArray(filters?.tags) ? filters!.tags.filter((tag): tag is string => typeof tag === 'string') : [],
  dialCodes: Array.isArray(filters?.dialCodes)
    ? filters!.dialCodes.filter(
        (dialCode): dialCode is ContactFilters['dialCodes'][number] =>
          typeof dialCode === 'object' &&
          dialCode !== null &&
          typeof dialCode.code === 'string' &&
          typeof dialCode.dialCode === 'string' &&
          /^\d+$/.test(dialCode.dialCode) &&
          typeof dialCode.name === 'string'
      )
    : [],
});

export const portalV2Api = createApi({
  tagTypes: ['ContactWithCampaign', 'ContactFilters', 'CampaignFilters', 'SMSCDRFilters', 'Campaign', 'Contact', 'CDR'],
  baseQuery: withSessionExpiredHandler(fetchBaseQuery({
    baseUrl: `${appConfig.URL_REST_V2}`,
    credentials: 'include',
    prepareHeaders: (headers, { type, endpoint }) => {
      const useSessionOnly =
        endpoint === 'bulkAssignTags' || endpoint === 'bulkUnassignTags';
      if (!useSessionOnly) {
        const token = typeof window !== 'undefined' ? window.localStorage.getItem('token') : null;
        if (token) {
          headers.set('Authorization', `Bearer ${token}`);
        }
      }
      if (type === 'mutation' && !headers.has('Content-Type')) {
        headers.set('Content-Type', 'application/json; charset=UTF-8');
      }
      return headers;
    },
  })),
  reducerPath: 'portalV2Api',
  endpoints: (builder) => ({
    getContactsWithCampaign: builder.query<ContactWithCampaign[], string>({
      query: (id) => ({
        url: `chat/clients/${id}/SMS`,
        params: {
          page: 0,
          size: 1000,
        },
      }),
      providesTags: (_result, _error, senderId) => [{ type: 'ContactWithCampaign', id: senderId }],
      transformResponse: (response: { content: ContactWithCampaign[] }) => response.content,
    }),
    /**
     * Bulk "All Sender IDs" client pull — single request across every sender ID of the
     * current customer (replaces the N+1 per-sender fan-out). Rows carry `senderNumber`.
     */
    getAllSmsContactsWithCampaign: builder.query<ContactWithCampaign[], { page?: number; size?: number } | void>({
      query: (args) => ({
        url: 'chat/clients/SMS',
        params: {
          page: args?.page ?? 0,
          size: args?.size ?? 1000,
        },
      }),
      providesTags: [{ type: 'ContactWithCampaign', id: 'ALL' }],
      transformResponse: (response: { content: ContactWithCampaign[] }) => response.content,
    }),
    getContactFilters: builder.query<ContactFilters, void>({
      async queryFn(_arg, _api, _extraOptions, baseQuery) {
        const primaryResult = await baseQuery({
          url: 'contacts/filters',
        });

        if (!primaryResult.error) {
          return {
            data: sanitizeContactFilters(primaryResult.data as ContactFilters),
          };
        }

        const contactsResult = await baseQuery({
          url: 'contacts',
          params: {
            page: 0,
            size: 200,
            full: true,
          },
        });

        let tags = contactsResult.error ? [] : normalizeTagsFromContactsResponse(contactsResult.data);

        if (!tags.length) {
          const legacyTagsResult = await baseQuery({
            url: `${appConfig.URL_REST}contacts/tags`,
            params: {
              full: true,
              page: 0,
              size: 1000,
            },
          });
          if (!legacyTagsResult.error) {
            tags = normalizeLegacyTagNames(legacyTagsResult.data);
          }
        }

        return {
          data: {
            tags: [...new Set(tags)],
            dialCodes: buildFallbackDialCodes(),
          },
        };
      },
      providesTags: [{ type: 'ContactFilters', id: 'contactFilters' }],
    }),
    getCampaignFilters: builder.query<CampaignFilters, void>({
      query: () => ({
        url: 'campaigns/filters',
      }),
      providesTags: [{ type: 'CampaignFilters', id: 'campaignFilters' }],
    }),
    getSMSCDRFitlers: builder.query<SMSCDRFilters, void>({
      query: () => ({
        url: 'cdr/sms/filters',
      }),
      providesTags: [{ type: 'SMSCDRFilters', id: 'cdrFilters' }],
    }),
    getCampaigns: builder.query<
      PaginatedResponse<'campaigns', Campaign>,
      {
        page: number;
        size: number;
        sort: string;
        channels?: string[];
        statuses?: string[];
        name?: string;
      }
    >({
      query: (params) => ({
        url: 'campaigns',
        params,
      }),
      providesTags: [{ type: 'Campaign', id: 'campaigns' }],
    }),
    searchCampaignsByName: builder.query<
      PaginatedResponse<'campaigns', Campaign>,
      {
        name: string;
        page?: number;
        size?: number;
        sort?: string;
        channels?: string[];
        statuses?: string[];
        fallbackExists?: boolean;
        creationDateFrom?: string;
        creationDateTo?: string;
        runDateFrom?: string;
        runDateTo?: string;
      }
    >({
      query: ({ name, page, size, sort, ...rest }) => ({
        url: 'campaigns/search',
        params: {
          name,
          ...(typeof page === 'number' ? { page } : {}),
          ...(typeof size === 'number' ? { size } : {}),
          ...(sort ? { sort } : {}),
          ...rest,
        },
      }),
      providesTags: [{ type: 'Campaign', id: 'campaigns' }],
    }),
    deleteCampaignV2: builder.mutation<void, number | string>({
      async queryFn(id, _queryApi, _extraOptions, fetchWithBQ) {
        const result = await fetchWithBQ({
          url: `campaigns/${id}`,
          method: 'DELETE',
          responseHandler: 'text',
        });

        if (result.error) {
          return { error: result.error };
        }

        return { data: undefined };
      },
      invalidatesTags: [{ type: 'Campaign', id: 'campaigns' }],
    }),
    getContacts: builder.query<
      PaginatedResponse<'contacts', Contact>,
      {
        mobilePhone?: string;
        page: number;
        size: number;
        sort?: string;
        full?: boolean;
        tags?: string[];
        dialCodes?: string[];
        firstName?: string;
        lastName?: string;
        companyName?: string;
      }
    >({
      query: (params) => ({
        url: 'contacts',
        params,
      }),
      providesTags: [{ type: 'Contact', id: 'contacts' }],
    }),
    searchContacts: builder.query<
      { contacts: Contact[]; page?: { totalElements?: number } },
      { query: string; companyName?: string; page?: number; size?: number }
    >({
      query: ({ query, companyName, page = 0, size = 200 }) => ({
        url: 'contacts/search',
        params: {
          query,
          ...(companyName ? { companyName } : {}),
          page,
          size,
        },
      }),
      providesTags: [{ type: 'Contact', id: 'contacts' }],
      transformResponse: (response: { _embedded?: { contacts?: Contact[] }; page?: { totalElements?: number } }) => ({
        contacts: response._embedded?.contacts || [],
        page: response.page,
      }),
    }),
    getSMSCDRs: builder.query<
      { content: CDR[]; page: { number: number; size: number; totalElements: number; totalPages: number } },
      {
        id: string;
        page: number;
        size: number;
        full: boolean;
        sort: string;
        senderIds?: string[];
        services?: string[];
        directions?: string[];
        statuses?: CDRStatus[];
        destinations?: string[];
        sendingTimeFrom?: string;
        sendingTimeTo?: string;
        statusTimeFrom?: string;
        statusTimeTo?: string;
        partsFrom?: number;
        partsTo?: number;
        rateFrom?: number;
        rateTo?: number;
      }
    >({
      query: ({ id, ...params }) => ({
        url: `cdr/sms/${id}`,
        params,
      }),
      providesTags: [{ type: 'CDR', id: 'cdrs' }],
      transformResponse: (response: unknown) => {
        const raw = response as Record<string, unknown>;
        const content = Array.isArray(raw?.content) ? raw.content : [];
        const number = (raw?.number as number) ?? 0;
        const size = (raw?.size as number) ?? 20;
        const numberOfElements = (raw?.numberOfElements as number) ?? content.length;
        const last = raw?.last === true;
        const pageObj = raw?.page as Record<string, unknown> | undefined;
        const totalElementsFromApi =
          (raw?.totalElements as number) ?? (pageObj?.totalElements as number);
        const totalElements =
          typeof totalElementsFromApi === 'number'
            ? totalElementsFromApi
            : last
              ? number * size + numberOfElements
              : (number + 2) * size;
        const totalPagesFromApi = (raw?.totalPages as number) ?? (pageObj?.totalPages as number);
        const totalPages =
          typeof totalPagesFromApi === 'number'
            ? totalPagesFromApi
            : last
              ? number + 1
              : number + 2;
        return {
          content,
          page: { number, size, totalElements, totalPages },
        };
      },
    }),
    bulkAssignTags: builder.mutation<
      { assignedContactIds: number[] },
      { contactIds: number[]; tagNames: string[] }
    >({
      query: (body) => ({
        url: 'contacts/tags/bulk/assign',
        method: 'PUT',
        body,
      }),
      invalidatesTags: [
        { type: 'Contact', id: 'contacts' },
        { type: 'ContactFilters', id: 'contactFilters' },
      ],
    }),
    bulkUnassignTags: builder.mutation<
      { assignedContactIds: number[] },
      { contactIds: number[]; tagNames: string[] }
    >({
      query: (body) => ({
        url: 'contacts/tags/bulk/unassign',
        method: 'PUT',
        body,
      }),
      invalidatesTags: [
        { type: 'Contact', id: 'contacts' },
        { type: 'ContactFilters', id: 'contactFilters' },
      ],
    }),
  }),
});

export const {
  useGetContactsWithCampaignQuery,
  useLazyGetContactsWithCampaignQuery,
  useGetAllSmsContactsWithCampaignQuery,
  useLazyGetAllSmsContactsWithCampaignQuery,
  useGetContactFiltersQuery,
  useGetCampaignFiltersQuery,
  useGetSMSCDRFitlersQuery,
  useGetCampaignsQuery,
  useLazyGetCampaignsQuery,
  useLazySearchCampaignsByNameQuery,
  useDeleteCampaignV2Mutation,
  useLazyGetContactsQuery,
  useLazyGetSMSCDRsQuery,
  useLazySearchContactsQuery,
  useBulkAssignTagsMutation,
  useBulkUnassignTagsMutation,
} = portalV2Api;
